import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material';
import { ArrowUpward, ArrowDownward, DragIndicator, Close } from '@mui/icons-material';

// Reusable "Column Options" dialog. The top section lists the currently active
// columns (removable and reorderable via buttons or drag); the bottom section
// lets the user add more columns from a pool of options. Changes are held in a
// local draft and only applied to the parent when the user clicks Save.
//
// Options are normalized to the shape:
//   { id, label, group?, field }
// where `label` is shown in the autocomplete, `group` (optional) enables
// grouped headings, and `field` is the object appended to the active list when
// the option is added. Active `fields` are objects with at least `{ id, label }`.
const ColumnOptionsDialog = ({
  open,
  onClose,
  title,
  fields,
  options,
  optionsLoading,
  onSave,
  grouped,
  minFields,
  requireOneOf,
  requireOneOfMessage,
  showRestoreDefault,
  defaultFields,
  emptyActiveText,
  addButtonLabel,
  selectLabel,
  allAddedText,
}) => {
  const [draftFields, setDraftFields] = useState(fields || []);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [selectedToAdd, setSelectedToAdd] = useState([]);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [requireBlockOpen, setRequireBlockOpen] = useState(false);

  // Re-sync the draft each time the dialog opens so an unsaved edit from a
  // previous open (closed via Close/discard) does not persist.
  useEffect(() => {
    if (open) {
      setDraftFields(fields || []);
      setSelectedToAdd([]);
      setConfirmDiscardOpen(false);
      setConfirmSaveOpen(false);
      setRequireBlockOpen(false);
    }
  }, [open, fields]);

  // Options the user can still add: the full pool minus anything already in the
  // active draft. Removing a field from the active list makes it reappear here.
  const addOptions = useMemo(() => {
    const activeIds = new Set(draftFields.map((field) => field.id));
    return (options || []).filter((option) => !activeIds.has(option.id));
  }, [draftFields, options]);

  // Dirty when the draft differs from the saved fields by content or order.
  const isDirty = useMemo(() => {
    const original = fields || [];
    if (draftFields.length !== original.length) return true;
    return draftFields.some((field, index) => field.id !== original[index].id);
  }, [draftFields, fields]);

  const moveField = (index, direction) => {
    setDraftFields((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const removeField = (index) => {
    setDraftFields((prev) => {
      if (prev.length <= minFields) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const resetDragState = () => {
    setDraggingIndex(null);
    setDragOverIndex(null);
  };

  const handleDragStart = (index) => (event) => {
    setDraggingIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    // Required for Firefox to initiate the drag.
    event.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (index) => (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (index !== dragOverIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (index) => (event) => {
    event.preventDefault();
    if (draggingIndex === null || draggingIndex === index) {
      resetDragState();
      return;
    }
    setDraftFields((prev) => {
      const next = [...prev];
      const [moved] = next.splice(draggingIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    resetDragState();
  };

  const handleAddFields = () => {
    if (selectedToAdd.length === 0) return;
    setDraftFields((prev) => [...prev, ...selectedToAdd.map((option) => option.field)]);
    setSelectedToAdd([]);
  };

  const handleRestoreDefault = () => {
    setDraftFields(defaultFields || []);
    setSelectedToAdd([]);
    resetDragState();
  };

  // Route every close intent through here so unsaved changes prompt a warning.
  const handleCloseRequest = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmDiscard = () => {
    setConfirmDiscardOpen(false);
    onClose();
  };

  // Enforce any "must keep at least one of these columns" rule, then persist.
  const validateAndSave = () => {
    if (
      requireOneOf.length > 0 &&
      !draftFields.some((field) => requireOneOf.includes(field.id))
    ) {
      setRequireBlockOpen(true);
      return;
    }
    onSave(draftFields);
    onClose();
  };

  const handleSave = () => {
    // Warn if the user picked fields in the autocomplete but never clicked
    // "Add", as those selections would be silently lost on save.
    if (selectedToAdd.length > 0) {
      setConfirmSaveOpen(true);
      return;
    }
    validateAndSave();
  };

  const handleConfirmSave = () => {
    setConfirmSaveOpen(false);
    validateAndSave();
  };

  const autocompleteProps = grouped
    ? {
        groupBy: (option) => option.group,
        renderGroup: (params) => (
          <li key={params.key}>
            <Box
              sx={{
                position: 'sticky',
                top: -8,
                zIndex: 1,
                px: 1.25,
                py: 0.75,
                fontSize: '1rem',
                fontWeight: 600,
                color: 'text.primary',
                bgcolor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'grey.300',
              }}
            >
              {params.group}
            </Box>
            <Box component="ul" sx={{ p: 0 }}>
              {params.children}
            </Box>
          </li>
        ),
      }
    : {};

  const showAllAddedText = !optionsLoading && addOptions.length === 0;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseRequest}
        fullWidth
        maxWidth="sm"
        aria-labelledby="column-options-dialog-title"
      >
        <DialogTitle id="column-options-dialog-title" sx={{ textAlign: 'center' }}>
          {title}
        </DialogTitle>
        {/* dividers makes this region scroll while the actions row stays pinned */}
        <DialogContent dividers>
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
            Active Columns
          </Typography>
          <Box>
            {draftFields.length === 0 ? (
              <Typography sx={{ textAlign: 'center', color: 'text.secondary', py: 1 }}>
                {emptyActiveText}
              </Typography>
            ) : (
              draftFields.map((field, index) => (
                <Box
                  key={field.id}
                  onDragOver={handleDragOver(index)}
                  onDrop={handleDrop(index)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 0.5,
                    borderBottom: '1px solid var(--mui-divider, #e0e0e0)',
                    borderTop:
                      dragOverIndex === index && draggingIndex !== null && draggingIndex !== index
                        ? '2px solid var(--primary-color, #1976d2)'
                        : '2px solid transparent',
                    opacity: draggingIndex === index ? 0.5 : 1,
                    backgroundColor:
                      dragOverIndex === index && draggingIndex !== index ? 'action.hover' : 'transparent',
                  }}
                >
                  <Tooltip title="Drag to reorder">
                    <Box
                      component="span"
                      draggable
                      onDragStart={handleDragStart(index)}
                      onDragEnd={resetDragState}
                      aria-hidden="true"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'grab',
                        color: 'text.secondary',
                        mr: 0.5,
                        '&:active': { cursor: 'grabbing' },
                      }}
                    >
                      <DragIndicator fontSize="small" />
                    </Box>
                  </Tooltip>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                    <Tooltip title="Move up">
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`move ${field.label} up`}
                          onClick={() => moveField(index, -1)}
                          disabled={index === 0}
                        >
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move down">
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`move ${field.label} down`}
                          onClick={() => moveField(index, 1)}
                          disabled={index === draftFields.length - 1}
                        >
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  <Typography sx={{ flexGrow: 1 }}>{field.label}</Typography>
                  <Tooltip title="Remove field" placement="left">
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`remove ${field.label}`}
                        onClick={() => removeField(index)}
                        disabled={draftFields.length <= minFields}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ))
            )}
          </Box>

          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
            Available Columns
          </Typography>
          {showAllAddedText ? (
            <Typography variant="body2">{allAddedText}</Typography>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Autocomplete
                multiple
                fullWidth
                size="small"
                disableCloseOnSelect
                loading={optionsLoading}
                options={addOptions}
                value={selectedToAdd}
                onChange={(event, newValue) => setSelectedToAdd(newValue)}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.label}
                  </li>
                )}
                {...autocompleteProps}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label={selectLabel}
                    placeholder="Search fields"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {optionsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddFields}
                disabled={selectedToAdd.length === 0}
                sx={{ whiteSpace: 'nowrap', mt: 0.25 }}
              >
                {addButtonLabel}
              </Button>
            </Box>
          )}
          <Box sx={{ pb: 2 }} />
        </DialogContent>
        <DialogActions sx={showRestoreDefault ? { justifyContent: 'space-between', px: 3 } : undefined}>
          {showRestoreDefault && (
            <Button onClick={handleRestoreDefault} variant="outlined">
              Restore Default
            </Button>
          )}
          <Box>
            <Button onClick={handleSave} variant="contained" color="primary">
              Save
            </Button>
            <Button onClick={handleCloseRequest} variant="outlined" sx={{ ml: 1 }}>
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDiscardOpen}
        onClose={() => setConfirmDiscardOpen(false)}
        aria-labelledby="discard-changes-dialog-title"
      >
        <DialogTitle id="discard-changes-dialog-title">Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved column changes. Closing now will discard them. Do you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDiscard} variant="contained" color="primary">
            Discard
          </Button>
          <Button onClick={() => setConfirmDiscardOpen(false)} variant="outlined">
            Keep Editing
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmSaveOpen}
        onClose={() => setConfirmSaveOpen(false)}
        aria-labelledby="save-unadded-dialog-title"
      >
        <DialogTitle id="save-unadded-dialog-title">Unsaved selected columns</DialogTitle>
        <DialogContent>
          <Typography>
            You have columns selected to add that have not been added yet. Saving now will discard
            them. Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmSave} variant="contained" color="primary">
            Continue
          </Button>
          <Button onClick={() => setConfirmSaveOpen(false)} variant="outlined">
            Keep Editing
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={requireBlockOpen}
        onClose={() => setRequireBlockOpen(false)}
        aria-labelledby="required-column-dialog-title"
      >
        <DialogTitle id="required-column-dialog-title">Required column</DialogTitle>
        <DialogContent>
          <Typography>{requireOneOfMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequireBlockOpen(false)} variant="contained" color="primary">
            Keep Editing
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ColumnOptionsDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
    })
  ),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      group: PropTypes.string,
      field: PropTypes.object,
    })
  ),
  optionsLoading: PropTypes.bool,
  onSave: PropTypes.func,
  grouped: PropTypes.bool,
  minFields: PropTypes.number,
  requireOneOf: PropTypes.arrayOf(PropTypes.string),
  requireOneOfMessage: PropTypes.string,
  showRestoreDefault: PropTypes.bool,
  defaultFields: PropTypes.array,
  emptyActiveText: PropTypes.string,
  addButtonLabel: PropTypes.string,
  selectLabel: PropTypes.string,
  allAddedText: PropTypes.string,
};

ColumnOptionsDialog.defaultProps = {
  title: 'Column Options',
  fields: [],
  options: [],
  optionsLoading: false,
  grouped: false,
  minFields: 0,
  requireOneOf: [],
  requireOneOfMessage: '',
  showRestoreDefault: false,
  defaultFields: [],
  emptyActiveText: 'No active fields. Add fields below.',
  addButtonLabel: 'Add Columns',
  selectLabel: 'Select Available Columns',
  allAddedText: 'All available columns have been added.',
};

export default ColumnOptionsDialog;
